import React, { useState, useEffect } from "react";
import { Box, Text, Flex } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { COLORS } from "../../globalvariables";
import { LANGUAGE_CHANGE_EVENT } from "../../context/LanguageContext";

const MotionBox = motion(Box);
const MotionImage = motion.img;
const MotionText = motion(Text);

// Props: title, teamMembersData (array of {name, role, image, description})
function TeamSection({ title, teamMembersData }) {
  const [hovered, setHovered] = useState(null);
  const [active, setActive] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  // Ensure we have valid data
  const teamMembers = Array.isArray(teamMembersData) ? teamMembersData : [];

  useEffect(() => {
    const handleClick = (e) => {
      const allowedSelectors = [
        ".card-active img", // image active
        ".card-active h2", // nom
        ".card-active h3", // poste
        ".card-active p", // texte
        ".inactive-image", // image de l'autre
      ];

      const isAllowed = allowedSelectors.some((selector) =>
        e.target.closest(selector)
      );

      if (!isAllowed) {
        setActive(null);
        setHovered(null);
      }
    };

    window.addEventListener("mousedown", handleClick);
    return () => window.removeEventListener("mousedown", handleClick);
  }, []);

  // Nouvelle fonction d'effet pour écouter les changements de langue
  useEffect(() => {
    const handleLanguageChange = () => {
      // Fermer la modale active lorsque la langue change
      setActive(null);
      setHovered(null);
    };

    // Ajouter l'écouteur d'événement
    document.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);

    // Nettoyage
    return () => {
      document.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const sectionElement = document.getElementById("team");
    if (sectionElement) {
      observer.observe(sectionElement);
    }

    return () => {
      if (sectionElement) {
        observer.unobserve(sectionElement);
      }
    };
  }, []);

  if (teamMembers.length === 0) {
    return (
      <Box color="white" textAlign="center" py={8}>
        No team members data available
      </Box>
    );
  }

  return (
    <Box
      w="100%"
      py={{ base: 8, md: 16 }}
      px={{ base: 2, md: 4 }}
      id="team"
      opacity={0.8}
    >
      {title && (
        <MotionText
          fontSize={{ base: "3xl", md: "5xl" }}
          fontWeight="bold"
          textAlign="center"
          mb={{ base: 8, md: 20 }}
          color={COLORS.creamText}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box
            as="div"
            height="3px"
            width="60px"
            bg={COLORS.primary}
            borderRadius="3px"
            mx="auto"
            mb={4}
          />
          {title}
        </MotionText>
      )}

      <Flex
        justify="center"
        align="center"
        wrap={{ base: "wrap", md: "nowrap" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 6, md: 8 }}
        position="relative"
      >
        {teamMembers.map((member, index) => {
          const isActive = active === index;
          const isHovered = hovered === index;

          return (
            <MotionBox
              key={index}
              flex={{ base: "1 1 100%", md: "1 1 45%" }}
              maxW={{ base: "300px", md: "400px" }}
              mx={{ base: 0, md: 8 }}
              cursor="pointer"
              onClick={() => setActive(isActive ? null : index)}
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
              className={isActive ? "card-active" : ""}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              textAlign="center"
            >
              <MotionBox
                w={{ base: "250px", md: "300px" }}
                h={{ base: "250px", md: "300px" }}
                borderRadius={isActive || isHovered ? "50%" : "20px"}
                overflow="hidden"
                border={`4px solid ${
                  isActive || isHovered ? COLORS.primary : COLORS.borderLight
                }`}
                mx="auto"
              >
                <MotionImage
                  src={member.image}
                  alt={member.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  animate={{
                    filter:
                      isActive || isHovered
                        ? "blur(0px)"
                        : active === null
                        ? "blur(4px)"
                        : "blur(6px)",
                  }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                />
              </MotionBox>

              <Text
                fontSize={isActive ? "3xl" : "2xl"}
                color={COLORS.textPrimary}
                mt={4}
              >
                {member.name}
              </Text>
              <Text
                fontSize={isActive ? "xl" : "lg"}
                color={COLORS.textPrimary}
              >
                {member.role}
              </Text>

              <Box
                maxH={isActive || isHovered ? "500px" : "0"}
                maxW={{ base: "90%", md: "70ch" }}
                mx="auto"
                opacity={isActive || isHovered ? 1 : 0}
                overflow="hidden"
                transition="all 0.5s ease"
                p={isActive || isHovered ? 6 : 0}
                bg={COLORS.primary}
                color={COLORS.creamText}
                borderRadius="lg"
                border="none"
                mt={2}
              >
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  textAlign="center"
                  lineHeight="1.6"
                >
                  {member.description}
                </Text>
              </Box>
            </MotionBox>
          );
        })}
      </Flex>
    </Box>
  );
}

export default TeamSection;
